import { ErrorResponse, IndexedDbDPoPStore, UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'
import { env } from './env'

export interface RequestCredentials { authorization: string; proof: string }
export interface ApiSession {
  authorize(url: string, method: string, nonce?: string): Promise<RequestCredentials | undefined>
  refresh(rejectedAuthorization?: string): Promise<boolean>
  login(options?: { redirectUri?: string }): Promise<void>
}

export function returnPath(value: unknown): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return '/'
  const parsed = new URL(value, window.location.origin)
  if (parsed.origin !== window.location.origin || ['/login', '/auth/callback'].includes(parsed.pathname)) return '/'
  return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

function requireBoundUser(user: User | null): User | null {
  if (user === null) return null
  if (user.token_type.toLowerCase() !== 'dpop') throw new Error('The identity provider did not issue a DPoP-bound session')
  return user
}

/** The library owns OAuth, PKCE, nonce challenges, key generation and signing. */
export class BrowserSession implements ApiSession {
  private initial?: Promise<User | null>
  private readonly lockName: string
  constructor(readonly manager: UserManager, private readonly locks: LockManager = navigator.locks) {
    this.lockName = `bdi-oidc:${manager.settings.authority}:${manager.settings.client_id}`
  }

  private async exclusive<T>(action: () => Promise<T>): Promise<T> {
    if (!this.locks) return Promise.reject(new Error('This browser requires HTTPS and Web Locks for secure authentication'))
    // Never fall back to unlocked token rotation. Every tab re-reads the shared session under this lock.
    return await this.locks.request(this.lockName, action)
  }

  initialize(): Promise<User | null> {
    return this.initial ??= this.exclusive(async () => {
      for (const key of ['kc_token', 'kc_refreshToken', 'kc_idToken']) localStorage.removeItem(key)
      await this.manager.clearStaleState()
      if (window.location.pathname === '/auth/callback') {
        const user = requireBoundUser(await this.manager.signinRedirectCallback())
        window.history.replaceState(null, '', returnPath(user?.state))
        return user
      }
      const user = await this.manager.getUser()
      if (user && user.token_type.toLowerCase() !== 'dpop') {
        await this.manager.removeUser()
        return null
      }
      return this.freshUser()
    })
  }

  private async freshUser(rejectedAuthorization?: string): Promise<User | null> {
    const user = requireBoundUser(await this.manager.getUser())
    if (!user) return null
    const rejectedCurrentToken = rejectedAuthorization === `DPoP ${user.access_token}`
    if (!rejectedCurrentToken && (user.expires_in ?? 0) > 30) return user
    if (!user.refresh_token) return null
    try {
      return requireBoundUser(await this.manager.signinSilent())
    } catch (error) {
      if (error instanceof ErrorResponse && (error.error === 'invalid_grant' || error.error === 'invalid_token')) {
        // Under the shared lock: queued requests/tabs must not retry a rejected grant.
        await this.manager.removeUser()
        return null
      }
      throw error
    }
  }

  async authorize(url: string, method: string, nonce?: string): Promise<RequestCredentials | undefined> {
    return this.exclusive(async () => {
      const user = await this.freshUser()
      if (!user) return undefined
      const target = new URL(url)
      target.search = ''
      target.hash = ''
      const proof = await this.manager.dpopProof(target.href, user, method.toUpperCase(), nonce)
      if (!proof) throw new Error('The browser session has lost its DPoP key; sign in again')
      return { authorization: `DPoP ${user.access_token}`, proof }
    })
  }

  async refresh(rejectedAuthorization?: string): Promise<boolean> {
    try { return await this.exclusive(async () => Boolean(await this.freshUser(rejectedAuthorization))) }
    catch { return false }
  }

  async login(options?: { redirectUri?: string }): Promise<void> {
    const url = new URL(options?.redirectUri ?? window.location.href, window.location.origin)
    const path = url.origin === window.location.origin ? returnPath(`${url.pathname}${url.search}${url.hash}`) : '/'
    // A redirect completes outside this document; the lock covers state/key creation only.
    await this.exclusive(() => this.manager.signinRedirect({ state: path }))
  }

  async logout(): Promise<void> {
    await this.exclusive(() => this.manager.signoutRedirect())
  }
}

let browserSession: BrowserSession | undefined
export function getBrowserSession(): BrowserSession {
  if (!browserSession) {
    if (!window.isSecureContext || !window.crypto.subtle) throw new Error('Secure browser authentication requires HTTPS')
    browserSession = new BrowserSession(new UserManager({
      authority: env.VITE_KEYCLOAK_URL,
      client_id: env.VITE_KEYCLOAK_CLIENT_ID,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: `${window.location.origin}/login`,
      response_type: 'code', response_mode: 'fragment', scope: 'openid profile email',
      loadUserInfo: false, automaticSilentRenew: false, monitorSession: false,
      requestTimeoutInSeconds: 15,
      // Shared session and key pair across tabs, all token mutations serialized by Web Locks.
      userStore: new WebStorageStateStore({ prefix: 'bdi.oidc.', store: window.localStorage }),
      stateStore: new WebStorageStateStore({ prefix: 'bdi.oidc.state.', store: window.sessionStorage }),
      dpop: { bind_authorization_code: true, store: new IndexedDbDPoPStore() },
    }))
  }
  return browserSession
}

export function userRoles(user: User | null): string[] {
  if (!user) return []
  try {
    // UI visibility only. Every operation is authorized again by the backend.
    const payload = user.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(atob(payload)) as { realm_access?: { roles?: unknown }; resource_access?: Record<string, { roles?: unknown }> }
    const candidates = [claims.realm_access?.roles, ...Object.values(claims.resource_access ?? {}).map(value => value.roles)]
    return [...new Set(candidates.flatMap(roles => Array.isArray(roles) ? roles.filter((role): role is string => typeof role === 'string') : []))]
  } catch { return [] }
}
